import * as React from "react"
import * as moment from "moment"
import {isEmpty as _isEmpty} from "lodash"
import {ModalComponent} from "./ModalComponent";
import {RadioBtnGroup} from "./common/RadioBtnGroup";
import CTA, {CTATheme} from "./common/CallToAction";
import NewTimeSlotPicker from "./common/NewTimeSlotPicker";
import AppUtils, {EpochConstants} from "../utils/AppUtils";
import {ApiManager} from "../api/ApiManager";
import {IDeleteAgentRoster} from "../models/Roster/RosterResponse";
import {AppConstants} from "../utils/AppConstants";

interface IClearSlotModalProps {
    slotStartTimeEpoch: number,
    slotEndTimeEpoch: number,
    closeModal: () => void,
    centerId: number,
    resourceId: number,
    handleSaveSuccess: (epochParams: {startTimeEpoch: number, endTimeEpoch: number}) => void,
    pageType: string
}

interface ISelect {
    label: any,
    value: any
}

const ClearSlotModal = (props: IClearSlotModalProps) => {
    const startTimeString = moment(props.slotStartTimeEpoch).format("hh:mm")
    const endTimeString = moment(props.slotEndTimeEpoch).format("hh:mm")
    const secondOption = moment(props.slotStartTimeEpoch).add(30, "m")
    const thirdOption = moment(props.slotStartTimeEpoch).add(1, "h")

    const [displayMoreOptions, setDisplayMoreOptions] = React.useState<boolean>(false)
    const [startTimeSlots, setStartTimeSlots] = React.useState<ISelect[]>([])
    const [endTimeSlots, setEndTimeSlots] = React.useState<ISelect[]>([])
    const [epochParams, setEpochParams] = React.useState({startTimeEpoch: null, endTimeEpoch: null})
    const [isSaving, setIsSaving] = React.useState<boolean>(false)

    const handleRadioOptionsClick = (val: string) => {
        if (val === "More") {
            setDisplayMoreOptions(true)
            setEpochParams({
                startTimeEpoch: startTimeSlots[0].value,
                endTimeEpoch: endTimeSlots[0].value
            })
        } else {
            if (displayMoreOptions) setDisplayMoreOptions(false)
            const [startTimeEpoch, endTimeEpoch] = val.split("-").map(ep => Number(ep))
            setEpochParams({startTimeEpoch, endTimeEpoch})
        }
    }

    const getTimeSlots = (epoch: number, timeLimit: number, interval?: number) => {
        let slots = []
        const currMinute = moment(epoch).minute()
        let t1 = currMinute < 30
            ? epoch + (30 - currMinute) * EpochConstants.EPOCH_IN_A_MIN
            : epoch + (60 - currMinute) * EpochConstants.EPOCH_IN_A_MIN

        while (t1 <= timeLimit) {
            slots.push({
                label: moment(t1).format("hh:mm A"),
                value: t1
            })
            t1 += 30 * EpochConstants.EPOCH_IN_A_MIN
        }
        return slots
    }

    React.useEffect(() => {
        let startTimeSlotIntervals: ISelect[] = [], endTimeSlotIntervals: ISelect[] = []
        const todaysEpochInfo = AppUtils.getDayEpochInfo(props.slotStartTimeEpoch)
        const startTimeLimit = todaysEpochInfo.endEpoch - 30 * EpochConstants.EPOCH_IN_A_MIN
        const endTimeLimit = todaysEpochInfo.endEpoch

        // start time slots
        startTimeSlotIntervals.push({
            label: moment(props.slotStartTimeEpoch).format("hh:mm A"),
            value: props.slotStartTimeEpoch
        })
        startTimeSlotIntervals = [...startTimeSlotIntervals, ...getTimeSlots(props.slotStartTimeEpoch, startTimeLimit)]

        // end time slots
        endTimeSlotIntervals.push({
            label: moment(props.slotEndTimeEpoch).format("hh:mm A"),
            value: props.slotEndTimeEpoch
        })
        endTimeSlotIntervals = [...endTimeSlotIntervals, ...getTimeSlots(props.slotEndTimeEpoch, endTimeLimit)]

        setStartTimeSlots(startTimeSlotIntervals)
        setEndTimeSlots(endTimeSlotIntervals)
    }, [])

    const handleSaveClick = () => {
        setIsSaving(true)
        const today = new Date(props.slotStartTimeEpoch)
        today.setUTCHours(0, 0, 0, 0)
        const jsonBody: IDeleteAgentRoster = {
            availaleOffline: true,
            availableOnline: true,
            centerId: props.centerId,
            endEpoch: epochParams.endTimeEpoch,
            endDayEpoch: 0,
            startDayEpoch: 0,
            resourceType: AppConstants.RESOURCE_TYPE_FOR_ROSTER,
            startEpoch: epochParams.startTimeEpoch,
            resourceId: props.resourceId,
            workingDayDate: today.toISOString()
        }
        ApiManager.getAlbusApis().deleteAgentRoster(jsonBody).then(() => {
            setIsSaving(false)
            props.handleSaveSuccess(epochParams)
        })
    }

    const valueIsSelected = AppUtils.isDefined(epochParams.startTimeEpoch) && AppUtils.isDefined(epochParams.endTimeEpoch)

    return (
        <ModalComponent modalSize={"x-small"} className={"clear-slot-modal"}>
            <div className={"clear-slot-header"}>Clear Slot?</div>
            <div className={"clear-slot-subheader"}>Clearing the slots will block your roster & mark you as unavailable.</div>
            <RadioBtnGroup items={[
                {displayValue: `This slot only (${startTimeString}-${endTimeString})`, internalValue: `${props.slotStartTimeEpoch}-${props.slotEndTimeEpoch}`},
                {displayValue: `30 Mins (${startTimeString}-${secondOption.format("hh:mm")})`, internalValue: `${props.slotStartTimeEpoch}-${secondOption.valueOf()}`},
                {displayValue: `60 Mins (${startTimeString}-${thirdOption.format("hh:mm")})`, internalValue: `${props.slotStartTimeEpoch}-${thirdOption.valueOf()}`},
                {displayValue: "More", internalValue: "More"}
            ]} handleClick={handleRadioOptionsClick}/>
            {
                displayMoreOptions && <div className={"clear-slot-more-options"}>
                    <div className={"option-cont"}>
                        <div className={"option-header"}>START TIME</div>
                        <NewTimeSlotPicker
                            options={startTimeSlots}
                            defaultValue={!_isEmpty(startTimeSlots) ? startTimeSlots[0] : null}
                            onChange={(item: ISelect) => setEpochParams({...epochParams, startTimeEpoch: item.value})}
                            menuPortalTarget={props.pageType === "CALENDAR" ? document.querySelector(".modal-backdrop") : document.body} />
                    </div>
                    <div className={"option-cont"}>
                        <div className={"option-header"}>END TIME</div>
                        <NewTimeSlotPicker
                            options={endTimeSlots}
                            defaultValue={!_isEmpty(endTimeSlots) ? endTimeSlots[0] : null}
                            onChange={(item: ISelect) => setEpochParams({...epochParams, endTimeEpoch: item.value})}
                            menuPortalTarget={props.pageType === "CALENDAR" ? document.querySelector(".modal-backdrop") : document.body} />
                    </div>
                </div>
            }
            <div className={"clear-slot-cta"}>
                <CTA innerText={"Cancel"} theme={CTATheme.SECONDARY} onClick={props.closeModal}/>
                <CTA innerText={isSaving ? "Saving..." : "Save"} theme={CTATheme.PRIMARY} disabled={!valueIsSelected} onClick={handleSaveClick}/>
            </div>
        </ModalComponent>
    )
}

export default ClearSlotModal